import React, { useState, useRef, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { 
  InformationCircleIcon, 
  QuestionMarkCircleIcon,
  PhoneIcon,
  DocumentTextIcon,
  ArrowDownIcon,
  ChevronDownIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import { Disclosure } from '@headlessui/react'
import { ContentWrapper } from '../components/ContentWrapper'
import { jsPDF } from 'jspdf'
import { format } from 'date-fns'
import { transferTaxData, calculateTransferTax } from '../utils/transferTaxData'
import { calculateClosingFee } from '../utils/titleFeeCalculations'
import { Helmet } from 'react-helmet-async'
import { SEO, createStructuredData } from '../components/SEO'

const schema = z.object({
  homePrice: z.number()
    .min(50000, 'Home price must be at least $50,000')
    .max(5000000, 'Home price cannot exceed $5,000,000'),
  downPaymentPercentage: z.number()
    .min(0, 'Down payment percentage must be 0%')
    .max(100, 'Down payment percentage cannot exceed 100%'),
  propertyType: z.enum(['singleFamily', 'condominium'], {
    required_error: 'Property type is required'
  }),
  loanType: z.enum(['conventional', 'fha', 'va', 'usda']),
  county: z.string().min(1, 'County is required'),
  city: z.string().min(1, 'City is required'),
})

type FormData = z.infer<typeof schema>

const illinoisCounties = [
  'Cook',
  'DuPage',
  'Kane',
  'Lake',
  'McHenry',
  'Will',
  // Add more counties as needed
]

const loanTypeInfo = {
  conventional: {
    title: 'Conventional Loan',
    description: 'A conventional loan is not backed or insured by the government, but it adheres to guidelines set by Fannie Mae and Freddie Mac. This is the most common mortgage option for homebuyers. See below for minimum requirements.',
    requirements: [
      'Minimum credit score of 620',
      'Down payment of 3% to 20%',
      'Debt-to-income ratio below 43%',
      'Stable income and employment history',
      'Private mortgage insurance (PMI) required for down payments less than 20%'
    ]
  },
  fha: {
    title: 'FHA Loan',
    description: 'A government-backed mortgage insured by the Federal Housing Administration, designed to help first-time homebuyers and those with lower credit scores.',
    requirements: [
      'Minimum credit score of 580',
      'Down payment as low as 3.5%',
      'Debt-to-income ratio up to 50%',
      'Primary residence only',
      'Mortgage insurance premium (MIP) required'
    ]
  },
  va: {
    title: 'VA Loan',
    description: 'A mortgage option for active-duty military personnel, veterans, and eligible surviving spouses, offering unique benefits and no down payment requirement.',
    requirements: [
      'Served 90 consecutive days active duty during war',
      'Served 181 days active duty during peacetime',
      'Six years in the Reserves or National Guard',
      'No down payment required',
      'No private mortgage insurance needed',
      'More flexible credit requirements'
    ]
  },
  usda: {
    title: 'USDA Loan',
    description: 'A zero-down-payment mortgage for rural and suburban homebuyers, backed by the U.S. Department of Agriculture.',
    requirements: [
      'Property must be in a USDA-eligible rural area',
      'Household income must not exceed 115% of the area median income',
      'Credit score of 640 or higher preferred',
      'Debt-to-income ratio below 41%',
      'Primary residence only',
      'No down payment required'
    ]
  }
}

export default function ClosingCosts() {
  const [homePrice, setHomePrice] = useState(350000)
  const [downPaymentPercentage, setDownPaymentPercentage] = useState(20)
  const [propertyType, setPropertyType] = useState<'singleFamily' | 'condominium'>('singleFamily')
  const [selectedCity, setSelectedCity] = useState('')
  const [availableCities, setAvailableCities] = useState<string[]>([])

  const [results, setResults] = useState<null | any>(null)
  const [showLoanInfo, setShowLoanInfo] = useState(false)
  const resultsRef = useRef<HTMLDivElement>(null);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const parseCurrency = (value: unknown): number => {
    // Handle different input types
    if (typeof value === 'number') return value
    if (typeof value === 'string') {
      // Remove currency symbols, commas, and trim
      const cleanedValue = value.replace(/[^0-9.-]+/g, '')
      const parsed = parseFloat(cleanedValue)
      return isNaN(parsed) ? 0 : parsed
    }
    return 0
  }

  const calculateDownPaymentAmount = (homePrice: number, percentage: number) => {
    return Math.round(homePrice * (percentage / 100))
  }

  const calculateHomeownerInsurance = (homePrice: number, propertyType: 'singleFamily' | 'condominium') => {
    const insuranceRate = propertyType === 'singleFamily' ? 0.006 : 0.003
    return Math.round(homePrice * insuranceRate)
  }

  const calculateEscrowDeposits = (homePrice: number, propertyType: 'singleFamily' | 'condominium') => {
    // Calculate homeowner's insurance escrow based on property type
    const insuranceRate = propertyType === 'singleFamily' ? 0.006 : 0.003
    const homeownersInsuranceEscrow = (homePrice * insuranceRate) / 6

    // Calculate property tax escrow (3 months)
    const propertyTaxEscrow = homePrice * 0.004

    return {
      propertyTaxEscrow,
      homeownersInsuranceEscrow,
    }
  }

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      homePrice: 350000,
      downPaymentPercentage: 20,
      propertyType: 'singleFamily',
      loanType: 'conventional',
      county: '',
      city: '',
    }
  })

  // Watch form values
  const watchHomePrice = watch('homePrice')
  const watchPropertyType = watch('propertyType')
  const watchLoanType = watch('loanType')

  // Update local state when form values change
  useEffect(() => {
    if (watchHomePrice !== homePrice) {
      setHomePrice(watchHomePrice)
    }
    if (watchPropertyType !== propertyType) {
      setPropertyType(watchPropertyType)
    }
  }, [watchHomePrice, watchPropertyType])

  useEffect(() => {
    const county = watch('county')
    if (county) {
      const countyData = transferTaxData.find(c => c.name === county)
      if (countyData) {
        setAvailableCities(countyData.cities.map(city => city.name))
        setValue('city', '') // Reset city when county changes
      }
    }
  }, [watch('county')])

  const defaultPrepaidItems = {
    propertyTaxes: 3600, // Example default value
    prepaidInterest: 1200, // Example default value
    taxServiceFee: 89 // Example default value
  };

  const calculateClosingCosts = (data: FormData) => {
    const { homePrice, downPaymentPercentage, propertyType, loanType, county, city } = data;
    const downPayment = calculateDownPaymentAmount(homePrice, downPaymentPercentage);
    const loanAmount = homePrice - downPayment;

    const escrowDeposits = calculateEscrowDeposits(homePrice, propertyType);
    const transferTax = calculateTransferTax(homePrice, county, city);

    const costs = {
      lenderFees: {
        underwritingFee: 995,
        processingFee: 495,
        appraisalFee: 600,
        creditReportFee: 100,
        floodCertificationFee: 20,
      },
      titleFees: {
        closingFee: calculateClosingFee(homePrice), // New dynamic closing fee
        titlePolicyLoan: 945,
        closingProtectionLetter: 50,
        policyUpdate: 150,
        chainOfTitle: 250,
        wireFee: 50,
        iapldServiceFee: 50,
        eRecordingFee: 25,
        emailFee: 25,
      },
      governmentFees: {
        transferTax,
        recordingFees: calculateRecordingTax(county),
      },
      prepaidItems: {
        homeownersInsurance: calculateHomeownerInsurance(homePrice, propertyType),
        propertyTaxes: defaultPrepaidItems.propertyTaxes,
        prepaidInterest: defaultPrepaidItems.prepaidInterest,
        taxServiceFee: defaultPrepaidItems.taxServiceFee,
        _includeInTotal: ['homeownersInsurance'] // Specify which items to include in total
      },
      escrowDeposits
    };

    // Calculate totals
    const totalLenderFees = calculateTotal(costs.lenderFees);
    const totalTitleFees = calculateTotal(costs.titleFees);
    const totalGovFees = calculateTotal(costs.governmentFees);
  
    // Only include homeowners insurance from prepaid items
    const totalPrepaid = costs.prepaidItems.homeownersInsurance;
  
    const totalEscrowDeposits = calculateTotal(costs.escrowDeposits);

    return {
      ...costs,
      totalClosingCosts: totalLenderFees + totalTitleFees + totalGovFees + totalPrepaid + totalEscrowDeposits,
      totalCashToClose: downPayment + totalLenderFees + totalTitleFees + totalGovFees + totalPrepaid + totalEscrowDeposits,
      downPayment,
      homePrice,
      county,
      loanType,
      propertyType,
    };
  };

  const calculateRecordingTax = (county: string) => {
    // Base recording tax for Illinois counties
    const baseFee = 75
    const countyFees: { [key: string]: number } = {
      'Cook': 100,
      'DuPage': 85,
    }
    return countyFees[county] || baseFee
  }

  const downloadBreakdown = () => {
    if (!results) return;

    const doc = new jsPDF();
    
    // Set document title
    doc.setFontSize(18);
    doc.text('Closing Costs Breakdown', 10, 20);
    
    // Add date
    doc.setFontSize(10);
    doc.text(`Generated on: ${format(new Date(), 'PPP')}`, 10, 30);

    // Basic details
    doc.setFontSize(12);
    doc.text(`Home Price: ${formatCurrency(results.homePrice)}`, 10, 50);
    doc.text(`County: ${results.county}`, 10, 60);
    doc.text(`Loan Type: ${results.loanType}`, 10, 70);

    // Detailed breakdown
    let yOffset = 90;
    doc.setFontSize(14);
    doc.text('Cost Breakdown', 10, yOffset);
    
    const categories = [
      { name: 'Lender Fees', costs: results.lenderFees },
      { name: 'Title Fees', costs: results.titleFees },
      { name: 'Government Fees', costs: results.governmentFees },
      { name: 'Prepaid Expenses', costs: results.prepaidItems },
      { name: 'Escrow Deposits', costs: results.escrowDeposits },
    ];

    yOffset += 10;
    doc.setFontSize(10);
    categories.forEach(category => {
      doc.text(`${category.name}: ${formatCurrency(Object.values(category.costs).reduce((a, b) => a + b, 0))}`, 10, yOffset);
      yOffset += 10;
    });

    // Total
    doc.setFontSize(12);
    doc.text(`Total Closing Costs: ${formatCurrency(results.totalClosingCosts)}`, 10, yOffset + 10);

    // Save the document
    doc.save(`closing_costs_breakdown_${format(new Date(), 'yyyyMMdd')}.pdf`);
  };

  // Helper function to calculate total, respecting the _includeInTotal flag
  const calculateTotal = (obj: Record<string, number>) => {
    return Object.entries(obj)
      .filter(([key, value]) => 
        // Exclude keys starting with underscore or any special flags
        !key.startsWith('_') && 
        // If _includeInTotal exists, only include specified keys
        (!obj._includeInTotal || obj._includeInTotal.includes(key))
      )
      .reduce((sum, [, value]) => sum + value, 0);
  };

  const onSubmit = (data: FormData) => {
    const closingCosts = calculateClosingCosts(data)
    setResults(closingCosts)
  }

  useEffect(() => {
    if (results && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [results]);

  const selectedLoanInfo = loanTypeInfo[watchLoanType]

  // Structured data object
  const closingCostsStructuredData = createStructuredData({
    "@type": "WebPage",
    "name": "Closing Costs Calculator for Home Buyers in Illinois",
    "description": "Comprehensive tool to estimate closing costs for home buyers in Illinois",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "7321 Ridgeway Avenue",
      "addressLocality": "Skokie",
      "addressRegion": "IL",
      "postalCode": "60076",
      "addressCountry": "US"
    },
    "mainEntity": {
      "@type": "FinancialService",
      "name": "Home Buyer Closing Costs Calculator",
      "description": "Calculate estimated closing costs for your home purchase in Illinois",
      "provider": {
        "@type": "Organization",
        "name": "Northside Legal",
        "url": "https://calculators.northsidelegal.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "7321 Ridgeway Avenue",
          "addressLocality": "Skokie",
          "addressRegion": "IL",
          "postalCode": "60076",
          "addressCountry": "US"
        }
      }
    }
  })

  return (
    <ContentWrapper>
      <SEO 
        title="Illinois Closing Costs Calculator | Home Buyer Closing Cost Estimator"
        description="Accurately estimate your home closing costs in Illinois. Free tool to calculate title fees, transfer taxes, and other essential closing expenses for home buyers."
        keywords={["closing costs calculator", "home buying costs", "Illinois real estate", "closing expenses", "home purchase fees"]}
        canonicalUrl="https://calculators.northsidelegal.com/closing-costs"
        structuredData={{
          "@type": "Calculator",
          "name": "Closing Costs Calculator for Illinois Home Buyers",
          "description": "Comprehensive tool to estimate closing costs for home buyers in Illinois",
          "url": "https://calculators.northsidelegal.com/closing-costs",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "7321 Ridgeway Avenue",
            "addressLocality": "Skokie",
            "addressRegion": "IL",
            "postalCode": "60076",
            "addressCountry": "US"
          },
          "provider": {
            "@type": "LegalService",
            "name": "Adler & Herbach Real Estate Attorneys",
            "url": "https://calculators.northsidelegal.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "7321 Ridgeway Avenue",
              "addressLocality": "Skokie",
              "addressRegion": "IL",
              "postalCode": "60076",
              "addressCountry": "US"
            },
            "telephone": "+1 (847) 318-3770",
            "priceRange": "$750"
          }
        }}
      />

      {/* SEO Content for Googlebot */}
      <div 
        style={{ 
          position: 'absolute', 
          width: '1px', 
          height: '1px', 
          padding: 0, 
          margin: '-1px', 
          overflow: 'hidden', 
          clip: 'rect(0, 0, 0, 0)', 
          whiteSpace: 'nowrap', 
          border: 0 
        }}
      >
        <h1>Illinois Closing Costs Calculator</h1>
        <p>Comprehensive guide to understanding closing costs in Illinois for home buyers.</p>
        
        <h2>What Are Closing Costs?</h2>
        <p>Closing costs are fees paid at the end of a real estate transaction, typically ranging from 2% to 5% of the home's purchase price.</p>
        
        <h3>Components of Closing Costs</h3>
        <ul>
          <li>Lender Fees: Underwriting, processing, and appraisal charges</li>
          <li>Title Insurance: Protecting property ownership rights</li>
          <li>Government Fees: Transfer taxes and recording fees</li>
          <li>Prepaid Expenses: Property taxes and homeowners insurance</li>
        </ul>
        
        <h3>Illinois-Specific Closing Costs</h3>
        <p>Closing costs in Illinois can vary based on county, city, and property type. Our calculator provides detailed estimates for major Illinois counties including Cook, DuPage, Kane, Lake, McHenry, and Will.</p>
      </div>

      <div className="card">
        <div className="p-6">
          <div>
            <h3 className="text-2xl font-display font-bold text-gray-900">
              Illinois Closing Cost Calculator
            </h3>
            <p className="mt-2 text-gray-500">
              Get an accurate estimate of your closing costs, including all fees and taxes specific to Illinois.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="calculator-form mt-8">
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-brand-700 mb-4">Property Details</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label htmlFor="propertyType" className="form-label">
                      Property Type
                      <span className="tooltip ml-1">
                        <QuestionMarkCircleIcon className="h-4 w-4 inline text-gray-400" />
                        <span className="tooltip-content -mt-1 ml-2">
                          Select the type of property for accurate insurance estimates
                        </span>
                      </span>
                    </label>
                    <select
                      {...register('propertyType')}
                      className="select-field"
                      value={propertyType}
                      onChange={(e) => {
                        const type = e.target.value as 'singleFamily' | 'condominium'
                        setPropertyType(type)
                        setValue('propertyType', type, {
                          shouldValidate: true,
                          shouldDirty: true
                        })
                      }}
                    >
                      <option value="singleFamily">Single Family Home</option>
                      <option value="condominium">Condominium</option>
                    </select>
                    {errors.propertyType && (
                      <p className="form-error">{errors.propertyType.message}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="county" className="form-label">
                      County
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      {...register('county')}
                      className="select-field"
                    >
                      <option value="">Select a county</option>
                      {transferTaxData.map(county => (
                        <option key={county.name} value={county.name}>
                          {county.name}
                        </option>
                      ))}
                    </select>
                    {errors.county && (
                      <p className="form-error">{errors.county.message}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="city" className="form-label">
                      City
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      {...register('city')}
                      className="select-field"
                      disabled={!watch('county')}
                    >
                      <option value="">Select a city</option>
                      {availableCities.map(city => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                    {errors.city && (
                      <p className="form-error">{errors.city.message}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-brand-700 mb-4">Loan Details</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label htmlFor="homePrice" className="form-label">
                      Home Price
                      <span className="tooltip ml-1">
                        <QuestionMarkCircleIcon className="h-4 w-4 inline text-gray-400" />
                        <span className="tooltip-content -mt-1 ml-2">
                          Enter the purchase price of the home
                        </span>
                      </span>
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                        $
                      </span>
                      <input
                        type="text"
                        value={formatCurrency(watch('homePrice')).replace('$', '')}
                        onChange={(e) => {
                          const value = parseCurrency(e.target.value)
                          setValue('homePrice', value, { 
                            shouldValidate: true,
                            shouldDirty: true
                          })
                        }}
                        className="input-field pl-7"
                        {...register('homePrice', { 
                          setValueAs: (v) => parseCurrency(v)
                        })}
                      />
                    </div>
                    {errors.homePrice && (
                      <p className="form-error">{errors.homePrice.message}</p>
                    )}
                  </div>

                  <div className="form-group mt-6">
                    <label className="form-label">
                      Down Payment
                      <span className="tooltip ml-1">
                        <QuestionMarkCircleIcon className="h-4 w-4 inline text-gray-400" />
                        <span className="tooltip-content -mt-1 ml-2">
                          The amount you plan to pay upfront
                        </span>
                      </span>
                    </label>
                    <div className="flex items-center space-x-4">
                      <div className="flex-grow">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="1"
                          value={downPaymentPercentage}
                          onChange={(e) => {
                            const percentage = parseInt(e.target.value, 10)
                            setDownPaymentPercentage(percentage)
                            setValue('downPaymentPercentage', percentage, {
                              shouldValidate: true,
                              shouldDirty: true
                            })
                          }}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>
                      <div className="w-1/4 text-right">
                        <div className="text-sm text-gray-600">
                          {downPaymentPercentage}% 
                          <span className="ml-2 text-gray-500">
                            ({formatCurrency(calculateDownPaymentAmount(homePrice, downPaymentPercentage))})
                          </span>
                        </div>
                      </div>
                    </div>
                    {errors.downPaymentPercentage && (
                      <p className="form-error">{errors.downPaymentPercentage.message}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="loanType" className="form-label">
                      Loan Type
                      <span className="tooltip ml-1">
                        <QuestionMarkCircleIcon className="h-4 w-4 inline text-gray-400" />
                        <span className="tooltip-content -mt-1 ml-2">
                          Different loan types have different requirements and benefits
                        </span>
                      </span>
                    </label>
                    <select
                      {...register('loanType')}
                      className="select-field"
                    >
                      <option value="conventional">Conventional</option>
                      <option value="fha">FHA</option>
                      <option value="va">VA</option>
                      <option value="usda">USDA</option>
                    </select>
                    {errors.loanType && (
                      <p className="form-error">{errors.loanType.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 space-y-4">
              <div className="flex justify-center">
                <button 
                  type="submit" 
                  className="btn-primary px-8 py-3 rounded-lg transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-50 shadow-lg"
                >
                  Calculate Closing Costs
                </button>
              </div>

              <Disclosure defaultOpen>
                {({ open }) => (
                  <>
                    <Disclosure.Button className="w-full flex justify-between items-center px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                      <div className="flex items-center">
                        <InformationCircleIcon className="h-4 w-4 text-brand-600 mr-2" />
                        <span className="font-medium text-gray-900">
                          About {selectedLoanInfo.title}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-brand-600 font-medium">
                          {open ? 'Click to collapse' : 'Click to expand'}
                        </span>
                        <ChevronDownIcon
                          className={`${open ? 'transform rotate-180' : ''} w-5 h-5 ml-2 text-gray-500`}
                        />
                      </div>
                    </Disclosure.Button>
                    <Disclosure.Panel className="bg-brand-50 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-brand-800">
                        {selectedLoanInfo.title}
                      </h4>
                      <p className="mt-2 text-gray-600">
                        {selectedLoanInfo.description}
                      </p>
                      <div className="mt-4">
                        <h5 className="font-medium text-brand-700">Requirements:</h5>
                        <ul className="mt-2 space-y-2">
                          {selectedLoanInfo.requirements.map((req, index) => (
                            <li key={index} className="flex items-center text-gray-600">
                              <CheckCircleIcon className="h-5 w-5 text-brand-500 mr-2" />
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            </div>
          </form>

          {results && (
            <div ref={resultsRef} className="results-section mt-8">
              <h4 className="results-header">Closing Cost Breakdown</h4>
              
              <div className="space-y-6">
                <Disclosure>
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="flex justify-between w-full px-4 py-3 bg-white rounded-lg shadow-sm hover:bg-gray-50 group transition-all duration-300 ease-in-out cursor-pointer">
                        <div className="flex items-center space-x-3 text-left">
                          <span className="font-medium text-brand-700 group-hover:text-brand-800">Lender Fees</span>
                          <span className="text-xs bg-brand-50 text-brand-600 px-2 py-0.5 rounded-full group-hover:bg-brand-100 hidden sm:inline-block">
                            Click to Expand
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-brand-600">
                            {formatCurrency(calculateTotal(results.lenderFees))}
                          </span>
                          <ChevronDownIcon 
                            className={`h-5 w-5 text-brand-500 transform transition-transform duration-300 ${
                              open ? 'rotate-180' : ''
                            }`} 
                          />
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="px-4 pt-4 pb-2 text-gray-600">
                        <div className="space-y-2 text-left">
                          {Object.entries(results.lenderFees || {}).map(([fee, amount]) => (
                            <div key={fee} className="flex justify-between text-sm">
                              <span className="capitalize">{fee.replace(/([A-Z])/g, ' $1').trim()}</span>
                              <span>{formatCurrency(amount as number)}</span>
                            </div>
                          ))}
                        </div>
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>

                <Disclosure>
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="flex justify-between w-full px-4 py-3 bg-white rounded-lg shadow-sm hover:bg-gray-50 group transition-all duration-300 ease-in-out cursor-pointer">
                        <div className="flex items-center space-x-3 text-left">
                          <span className="font-medium text-brand-700 group-hover:text-brand-800">Title & Settlement Fees</span>
                          <span className="text-xs bg-brand-50 text-brand-600 px-2 py-0.5 rounded-full group-hover:bg-brand-100 hidden sm:inline-block">
                            Click to Expand
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-brand-600">
                            {formatCurrency(calculateTotal(results.titleFees))}
                          </span>
                          <ChevronDownIcon 
                            className={`h-5 w-5 text-brand-500 transform transition-transform duration-300 ${
                              open ? 'rotate-180' : ''
                            }`} 
                          />
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="px-4 pt-4 pb-2 text-gray-600">
                        <div className="space-y-2 text-left">
                          {Object.entries(results.titleFees || {}).map(([key, value]) => {
                            const displayName = key === 'iapldServiceFee' ? 'IAPLD Service Fee' :
                              key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                            return (
                              <div key={key} className="flex justify-between text-sm">
                                <span className="capitalize">{displayName}</span>
                                <span>{formatCurrency(value as number)}</span>
                              </div>
                            );
                          })}
                        </div>
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>

                <Disclosure>
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="flex justify-between w-full px-4 py-3 bg-white rounded-lg shadow-sm hover:bg-gray-50 group transition-all duration-300 ease-in-out cursor-pointer">
                        <div className="flex items-center space-x-3 text-left">
                          <span className="font-medium text-brand-700 group-hover:text-brand-800">Government Fees</span>
                          <span className="text-xs bg-brand-50 text-brand-600 px-2 py-0.5 rounded-full group-hover:bg-brand-100 hidden sm:inline-block">
                            Click to Expand
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-brand-600">
                            {formatCurrency(calculateTotal(results.governmentFees))}
                          </span>
                          <ChevronDownIcon 
                            className={`h-5 w-5 text-brand-500 transform transition-transform duration-300 ${
                              open ? 'rotate-180' : ''
                            }`} 
                          />
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="px-4 pt-4 pb-2 text-gray-600">
                        <div className="space-y-2 text-left">
                          {Object.entries(results.governmentFees || {}).map(([fee, amount]) => (
                            <div key={fee} className="flex justify-between text-sm">
                              <span className="capitalize">{fee.replace(/([A-Z])/g, ' $1').trim()}</span>
                              <span>{formatCurrency(amount as number)}</span>
                            </div>
                          ))}
                        </div>
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>

                <Disclosure defaultOpen={true}>
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="w-full flex justify-between items-center px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                        <div className="flex items-center">
                          <span className="font-medium text-gray-900">Prepaid Items</span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-brand-600 font-medium">
                            {formatCurrency(results.prepaidItems.homeownersInsurance)}
                          </span>
                          <ChevronDownIcon
                            className={`${open ? 'transform rotate-180' : ''} w-5 h-5 ml-2 text-gray-500`}
                          />
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="px-4 pt-4 pb-2 text-sm text-gray-600">
                        <div className="space-y-4">
                          <div className="flex justify-between items-center">
                            <span>Homeowner's Insurance</span>
                            <span>{formatCurrency(results.prepaidItems.homeownersInsurance)}</span>
                          </div>
                          
                          {/* Blurred items with info message */}
                          <div className="relative">
                            <div className="filter blur-sm pointer-events-none">
                              <div className="flex justify-between items-center">
                                <span>Property Taxes</span>
                                <span>{formatCurrency(defaultPrepaidItems.propertyTaxes)}</span>
                              </div>
                              <div className="flex justify-between items-center mt-2">
                                <span>Prepaid Interest</span>
                                <span>{formatCurrency(defaultPrepaidItems.prepaidInterest)}</span>
                              </div>
                              <div className="flex justify-between items-center mt-2">
                                <span>Tax Service Fee</span>
                                <span>{formatCurrency(defaultPrepaidItems.taxServiceFee)}</span>
                              </div>
                            </div>
                            
                            {/* Call-to-action message */}
                            <div className="mt-4 p-4 bg-brand-50 rounded-lg">
                              <p className="text-brand-800 font-medium mb-2">Need a precise quote?</p>
                              <p className="text-gray-600 text-sm mb-3">
                                These items vary by municipality and require property-specific data. Call us for an accurate calculation of your property taxes, prepaid interest, and tax service fee.
                              </p>
                              <a
                                href="tel:+18474859400"
                                className="inline-flex items-center text-brand-600 hover:text-brand-700 font-medium"
                              >
                                <PhoneIcon className="h-4 w-4 mr-2" />
                                Call (847) 485-9400
                              </a>
                            </div>
                          </div>
                        </div>
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>

                <Disclosure>
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="flex justify-between w-full px-4 py-3 bg-white rounded-lg shadow-sm hover:bg-gray-50 group transition-all duration-300 ease-in-out cursor-pointer">
                        <div className="flex items-center space-x-3 text-left">
                          <span className="font-medium text-brand-700 group-hover:text-brand-800">Escrow Deposits</span>
                          <span className="text-xs bg-brand-50 text-brand-600 px-2 py-0.5 rounded-full group-hover:bg-brand-100 hidden sm:inline-block">
                            Click to Expand
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-brand-600">
                            {formatCurrency(calculateTotal(results.escrowDeposits))}
                          </span>
                          <ChevronDownIcon 
                            className={`h-5 w-5 text-brand-500 transform transition-transform duration-300 ${
                              open ? 'rotate-180' : ''
                            }`} 
                          />
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="px-4 pt-4 pb-2 text-gray-600">
                        <div className="space-y-2 text-left">
                          {Object.entries(results.escrowDeposits || {}).map(([item, amount]) => (
                            <div key={item} className="flex justify-between text-sm">
                              <span className="capitalize">{item.replace(/([A-Z])/g, ' $1').trim()}</span>
                              <span>{formatCurrency(amount as number)}</span>
                            </div>
                          ))}
                        </div>
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              </div>

              <div className="mt-8 p-6 bg-brand-700 rounded-lg text-white">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-brand-100">Total Closing Costs</span>
                    <span className="text-xl font-semibold">{formatCurrency(results.totalClosingCosts || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-brand-100">Down Payment</span>
                    <span className="text-xl font-semibold">{formatCurrency(results.downPayment || 0)}</span>
                  </div>
                  <div className="pt-4 border-t border-brand-600">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold">Total Cash to Close</span>
                      <span className="text-2xl font-bold">{formatCurrency(results.totalCashToClose || 0)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 space-y-4">
                <div className="p-6 bg-brand-50 rounded-lg">
                  <h5 className="text-lg font-semibold text-brand-800">Need help understanding these costs?</h5>
                  <p className="mt-2 text-gray-600">
                    Our experienced real estate attorneys can explain every fee and help you navigate the closing process.
                  </p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-4">
                    <a href="tel:847-318-3770" className="btn-primary flex-1">
                      <PhoneIcon className="h-4 w-4 mr-2" />
                      Schedule Free Consultation
                    </a>
                    <button 
                      onClick={downloadBreakdown} 
                      className="btn-secondary flex-1"
                    >
                      <DocumentTextIcon className="h-5 w-5 mr-2" />
                      Download Breakdown
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white py-12 px-4 sm:px-6 lg:px-8 mt-16">
        <div className="max-w-3xl mx-auto prose prose-lg">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">
            Everything You Need to Know About Closing Costs in Illinois
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Buying a home is an exciting journey, but understanding the financial details—like closing costs—is crucial. 
            Our <strong>Illinois Closing Cost Calculator</strong> is designed to provide accurate estimates for buyers 
            across the state, from Chicago to Naperville and beyond. This guide breaks down what you need to know about closing costs and how to plan for them.
          </p>

          <hr className="my-8" />

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">What Are Closing Costs?</h3>
          <p>
            Closing costs are the fees and expenses you pay when finalizing a real estate transaction. 
            These costs go beyond the home's purchase price and can vary depending on the property location, 
            loan type, and other factors.
          </p>

          <p className="mt-4">For Illinois homebuyers, closing costs typically include:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Lender Fees</strong>: Charges for processing your loan</li>
            <li><strong>Title Insurance & Settlement Fees</strong>: Protecting your ownership rights</li>
            <li><strong>Government Fees</strong>: Transfer taxes and recording fees</li>
            <li><strong>Prepaid Expenses</strong>: Property taxes, homeowners insurance, and more</li>
          </ul>

          <p className="mt-4">Our calculator simplifies this process by giving you a detailed breakdown of all these costs.</p>

          <hr className="my-8" />

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Why Are Closing Costs Important?</h3>
          <p>
            Understanding your closing costs helps you prepare financially and avoid surprises at the closing table. 
            These costs can add up to 2%–5% of your home's purchase price, so knowing what to expect is essential for budgeting.
          </p>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6">
            <p className="text-blue-700">For example:</p>
            <ul className="list-disc pl-5 mt-2 text-blue-700">
              <li>On a $300,000 home in Cook County: $6,000–$15,000</li>
              <li>These include transfer taxes, lender fees, and title insurance</li>
            </ul>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Breaking Down Illinois Closing Costs</h2>
          <p>Illinois has unique requirements that impact closing costs. Here's a closer look at the key components:</p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Lender Fees</h3>
          <p>These are fees charged by your mortgage lender for processing your loan. Common lender fees include:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Underwriting Fee</strong>: Covers the cost of evaluating your loan application</li>
            <li><strong>Origination Fee</strong>: A percentage of your loan amount (usually 0.5%–1%)</li>
            <li><strong>Credit Report Fee</strong>: A small fee for pulling your credit report</li>
          </ul>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Title Insurance & Settlement Fees</h3>
          <p>Title insurance protects you against disputes over property ownership. In Illinois:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Title insurance is required by most lenders</li>
            <li>Settlement fees cover services like escrow management and document preparation</li>
          </ul>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Government Fees</h3>
          <p>Illinois has some of the highest government fees in the U.S., including:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>State Transfer Tax</strong>: $0.50 per $500 of the property value</li>
            <li><strong>County Transfer Tax</strong>: Varies by county (e.g., $0.25 per $500 in Cook County)</li>
            <li><strong>City Transfer Tax</strong>: Additional charges for cities like Chicago ($3 per $1,000)</li>
          </ul>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Prepaid Expenses</h3>
          <p>These are upfront payments for recurring expenses like:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Property taxes (pro-rated based on your closing date)</li>
            <li>Homeowners insurance (typically one year upfront)</li>
          </ul>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Do You Need an Attorney for Your Home Purchase in Illinois?</h2>
          <p>
            While hiring an attorney is not legally required in Illinois, it's highly recommended. Real estate transactions 
            involve complex legal documents that only attorneys can draft or modify. Here's why having an attorney matters:
          </p>

          <ol className="list-decimal pl-5 space-y-4 mt-4">
            <li>
              <strong>Contract Review</strong><br />
              Attorneys ensure that purchase agreements are fair and protect your interests.
            </li>
            <li>
              <strong>Resolving Title Issues</strong><br />
              They identify and resolve title defects or liens before closing.
            </li>
            <li>
              <strong>Closing Process Oversight</strong><br />
              Attorneys review all documents at closing to ensure accuracy and compliance.
            </li>
            <li>
              <strong>Peace of Mind</strong><br />
              An attorney provides expert guidance, helping you avoid costly mistakes.
            </li>
          </ol>

          <p className="mt-4">
            Attorney fees typically range from $500–$1,500 but are well worth the investment for peace of mind during such 
            a significant transaction.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">First-Time Buyer Programs in Illinois</h2>
          <p>
            If you're a first-time buyer in Illinois, you may qualify for assistance programs that reduce your out-of-pocket expenses:
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Popular Programs:</h3>
          <ol className="list-decimal pl-5 space-y-4">
            <li>
              <strong>SmartBuy Program</strong><br />
              Offers up to $10,000 in down payment and closing cost assistance.
            </li>
            <li>
              <strong>1stHomeIllinois Program</strong><br />
              Provides forgivable loans for eligible buyers.
            </li>
            <li>
              <strong>IHDA Grants</strong><br />
              Helps with down payments or prepaid expenses.
            </li>
          </ol>

          <p className="mt-4">Check with your lender or local housing authority to see if you qualify.</p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">How to Use Our Illinois Closing Cost Calculator</h2>
          <p>Our calculator is simple to use and provides instant results tailored to Illinois buyers:</p>

          <ol className="list-decimal pl-5 space-y-2 mt-4">
            <li>Enter your property details (type, price, location)</li>
            <li>Specify your loan details (down payment percentage, loan type)</li>
            <li>Click "Calculate Closing Costs" to view a detailed breakdown of all fees</li>
          </ol>

          <p className="mt-4">
            Whether you're buying a single-family home in Chicago or a condo in Naperville, our tool gives you accurate 
            estimates so you can plan confidently.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Common Questions About Closing Costs</h2>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">How Much Are Closing Costs in Illinois?</h3>
          <p>
            Closing costs typically range from 2%–5% of the home's purchase price. For example:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>On a $250,000 home in DuPage County: $5,000–$12,500</li>
            <li>On a $400,000 home in Chicago: $8,000–$20,000</li>
          </ul>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Why Are Closing Costs Higher in Illinois?</h3>
          <p>
            Illinois has additional expenses like state transfer taxes ($0.50 per $500) and city-specific taxes 
            (e.g., Chicago adds $3 per $1,000). These extra charges make Illinois one of the costlier states for 
            real estate transactions.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Can Sellers Help Cover Closing Costs?</h3>
          <p>
            Yes! Buyers can negotiate with sellers to cover part or all of their closing costs through seller concessions.
          </p>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 my-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Ready to Estimate Your Closing Costs?</h2>
            <p className="text-blue-700">
              Our <strong>Illinois Closing Cost Calculator</strong> is designed specifically for buyers like you! 
              Whether you're purchasing in Cook County or anywhere else in Illinois, our tool provides accurate 
              estimates tailored to your needs.
            </p>
            <p className="text-blue-700 mt-4 font-bold">
              Take control of your home-buying journey today—calculate your closing costs now!
            </p>
          </div>
        </div>
      </div>
    </ContentWrapper>
  )
}
